"use client";

import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { FileSpreadsheet, Monitor, Phone, QrCode, LayoutGrid, ShieldCheck, ShieldOff } from "lucide-react";
import type { LucideIcon } from "lucide-react";

/**
 * Showcase op de landing: een dashboard-mockup waarin een opdrachtgever in één
 * oogopslag ziet via welk kanaal jobs zijn binnengekomen — Excel-upload, online
 * formulier, telefonisch of via een QR-code. Statische mockup (geen API).
 */

type Channel = {
  key: string;
  label: string;
  count: number;
  icon: LucideIcon;
  color: string;
};

const CHANNELS: Channel[] = [
  { key: "excel", label: "Excel-upload", count: 84, icon: FileSpreadsheet, color: "#14C07A" },
  { key: "formulier", label: "Online formulier", count: 132, icon: Monitor, color: "#4A5BD9" },
  { key: "telefoon", label: "Telefonisch", count: 47, icon: Phone, color: "#E3852A" },
  { key: "qr", label: "QR-code", count: 23, icon: QrCode, color: "#0B0E0C" },
];

const TOTAAL = CHANNELS.reduce((s, c) => s + c.count, 0);

type Status = "gepubliceerd" | "toegewezen" | "geaccepteerd";

const RECENT: { nr: string; apparaat: string; merk: string; kanaal: string; garantie: boolean; status: Status }[] = [
  { nr: "EP-0542", apparaat: "Wasmachine", merk: "Bosch", kanaal: "excel", garantie: true, status: "gepubliceerd" },
  { nr: "EP-0311", apparaat: "Vaatwasser", merk: "Siemens", kanaal: "formulier", garantie: false, status: "toegewezen" },
  { nr: "EP-0188", apparaat: "Droger", merk: "AEG", kanaal: "telefoon", garantie: true, status: "geaccepteerd" },
  { nr: "EP-0421", apparaat: "Oven", merk: "Whirlpool", kanaal: "qr", garantie: false, status: "geaccepteerd" },
];

const STATUS_PILL: Record<Status, string> = {
  gepubliceerd: "bg-[#EEF0FF] text-[#4A5BD9]",
  toegewezen: "bg-[#F1EFE9] text-[var(--ink-2)]",
  geaccepteerd: "bg-[#E6FAF1] text-[#0D7A4A]",
};

export default function JobsIntakeShowcase() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const byKey = Object.fromEntries(CHANNELS.map((c) => [c.key, c]));

  return (
    <section className="mx-auto w-full max-w-5xl px-5 py-14 sm:px-6 sm:py-24">
      <p className="mb-3 text-center font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)] sm:text-[11px]">
        Voor opdrachtgevers
      </p>
      <h2 className="mb-3 text-center text-[clamp(24px,4vw,36px)] font-semibold leading-tight tracking-[-0.025em] text-[var(--ink)] sm:mb-4">
        Jobs eenvoudig plaatsen!
      </h2>
      <p className="mx-auto mb-7 max-w-xl text-center text-[14px] leading-[1.65] text-[var(--ink-2)] sm:mb-12 sm:text-[15px] sm:leading-[1.7]">
        Plaats jobs zoals het jou uitkomt — bulk via Excel, met het online
        formulier, telefonisch of via een QR-code. Alles komt samen op één
        overzicht.
      </p>

      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 16 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--surface-raised)] shadow-[0_10px_36px_-16px_rgba(11,14,12,0.12)]"
      >
        {/* Dashboard-kop */}
        <div className="flex items-center justify-between gap-3 border-b border-[var(--line)] bg-[var(--surface)] px-4 py-3.5 sm:px-6 sm:py-4">
          <div className="flex items-center gap-2">
            <LayoutGrid size={16} strokeWidth={1.75} className="text-[var(--ink-3)]" />
            <span className="text-[14px] font-semibold text-[var(--ink)]">Mijn jobs</span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-[18px] font-extrabold tabular-nums text-[var(--ink)]">{TOTAAL}</span>
            <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-[var(--ink-3)] sm:text-[10px]">
              geplaatst · 30 dagen
            </span>
          </div>
        </div>

        {/* Kanaal-kaarten */}
        <div className="grid grid-cols-2 gap-2.5 p-4 sm:gap-3 sm:p-6 lg:grid-cols-4">
          {CHANNELS.map((c, i) => {
            const share = Math.round((c.count / TOTAAL) * 100);
            const Icon = c.icon;
            return (
              <div key={c.key} className="rounded-xl border border-[var(--line)] bg-[var(--surface)] p-3 sm:p-4">
                <span
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg sm:h-9 sm:w-9"
                  style={{ backgroundColor: `${c.color}1A`, color: c.color }}
                >
                  <Icon size={17} strokeWidth={1.75} />
                </span>
                <p className="mt-2.5 text-[19px] font-extrabold tabular-nums leading-none text-[var(--ink)] sm:mt-3 sm:text-[22px]">
                  {c.count}
                </p>
                <p className="mt-1 text-[11px] leading-tight text-[var(--ink-2)] sm:text-[12px]">{c.label}</p>
                <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-[var(--surface-sunk)]">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={inView ? { width: `${share}%` } : {}}
                    transition={{ duration: 0.7, ease: "easeOut", delay: 0.15 + i * 0.08 }}
                    className="h-full rounded-full"
                    style={{ backgroundColor: c.color }}
                  />
                </div>
                <p className="mt-1 font-mono text-[10px] text-[var(--ink-3)]">{share}%</p>
              </div>
            );
          })}
        </div>

        {/* Recente jobs */}
        <div className="border-t border-[var(--line)] px-4 pb-4 pt-4 sm:px-6 sm:pb-6 sm:pt-5">
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)] sm:mb-3">
            Recent geplaatst
          </p>
          <div className="flex flex-col">
            {RECENT.map((r) => {
              const ch = byKey[r.kanaal];
              const Icon = ch.icon;
              return (
                /* Mobiel twee regels (jobnr + status boven, apparaat + iconen onder);
                   vanaf sm het originele 5-koloms grid via `order`-reset + `contents`. */
                <div
                  key={r.nr}
                  className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1 border-b border-[var(--line)] py-2.5 last:border-0 sm:grid-cols-[56px_1fr_28px_28px_104px] sm:gap-3"
                >
                  <span className="order-1 font-mono text-[12px] tabular-nums text-[var(--ink-3)] sm:order-none">
                    {r.nr}
                  </span>
                  <span className="order-3 min-w-0 truncate text-[13px] text-[var(--ink)] sm:order-none">
                    {r.apparaat} · {r.merk}
                  </span>
                  <span className="order-4 flex items-center justify-end gap-2.5 sm:contents">
                    <span
                      title={r.garantie ? "Binnen garantie" : "Buiten garantie"}
                      className="inline-flex items-center justify-center sm:justify-self-center"
                      style={{ color: r.garantie ? "#0D7A4A" : "var(--ink-3)" }}
                    >
                      {r.garantie ? (
                        <ShieldCheck size={14} strokeWidth={1.75} />
                      ) : (
                        <ShieldOff size={14} strokeWidth={1.75} />
                      )}
                    </span>
                    <span
                      title={ch.label}
                      className="inline-flex items-center justify-center sm:justify-self-center"
                      style={{ color: ch.color }}
                    >
                      <Icon size={14} strokeWidth={1.75} />
                    </span>
                  </span>
                  <span
                    className={`order-2 justify-self-end rounded-full px-2.5 py-1 text-center text-[11px] font-medium sm:order-none sm:justify-self-start ${STATUS_PILL[r.status]}`}
                  >
                    {r.status}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
